import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth, useUser, useOrganization, useClerk } from '@clerk/clerk-react';
import { useDispatch } from 'react-redux';
import { Loader } from 'semantic-ui-react';

import actions from '../actions';
import Config from '../constants/Config';
import { setAccessToken } from '../utils/access-token-storage';

/**
 * ClerkLoginWrapper - Reemplazo profesional del LoginWrapper tradicional
 * Maneja completamente la autenticación con Clerk siguiendo el flujo de Redux
 */
const ClerkLoginWrapper = React.memo(({ isInitializing }) => {
  const { isLoaded: isClerkLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { redirectToSignIn } = useClerk();
  const dispatch = useDispatch();

  // Efecto para redirigir a páginas nativas de Clerk si no está autenticado
  useEffect(() => {
    if (isClerkLoaded && !isSignedIn) {
      console.log('🔄 Redirecting to Clerk hosted login page...');
      redirectToSignIn({
        redirectUrl: window.location.origin + '/',
        // signUpUrl: 'https://key-mullet-21.accounts.dev/sign-up', // DEPRECATED: Let Clerk handle it
        // Configuración de appearance para páginas hosted
        appearance: {
          layout: {
            logoImageUrl: window.location.origin + '/shakkii logo multicolor.jpeg',
            applicationName: 'Shakkii',
            logoPlacement: 'inside',
          },
          variables: {
            colorPrimary: '#4F81E7',
            colorBackground: '#ffffff',
            borderRadius: '8px',
          },
        },
      });
    }
  }, [isClerkLoaded, isSignedIn, redirectToSignIn]);

  // Efecto para manejar autenticación exitosa
  useEffect(() => {
    const handleClerkAuth = async () => {
      if (isSignedIn && user) {
        try {
          console.log('🔄 Clerk user authenticated, syncing with system...');

          // Llamar al backend para crear access token tradicional
          const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
          const userData = {
            clerkUserId: user.id,
            email: user.primaryEmailAddress?.emailAddress,
            name:
              fullName ||
              user.username ||
              user.primaryEmailAddress?.emailAddress?.split('@')[0] ||
              'Usuario',
            username: user.username || user.primaryEmailAddress?.emailAddress?.split('@')[0],
            organizationId: organization?.id,
            organizationName: organization?.name,
            organizationSlug: organization?.slug,
          };

          console.log('📤 Sending user data to backend:', userData);

          const apiBaseUrl = Config.SERVER_BASE_URL;

          // Usar el endpoint que sigue el patrón tradicional
          const response = await fetch(`${apiBaseUrl}/api/access-tokens/clerk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(userData),
          });

          console.log('📥 Backend response status:', response.status);

          if (!response.ok) {
            const errorText = await response.text();
            console.log('📥 Backend error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          const rawBody = await response.text();

          let accessToken;
          try {
            ({ item: accessToken } = JSON.parse(rawBody));
          } catch (parseError) {
            console.error('❌ Failed to parse Clerk token response:', rawBody);
            throw parseError;
          }

          // Setear token tradicional
          await setAccessToken(accessToken);

          // Disparar autenticación exitosa (esto iniciará el flujo normal)
          dispatch(actions.authenticate.success(accessToken));

          console.log('✅ Clerk integration completed successfully');
        } catch (error) {
          console.error('❌ Error in Clerk authentication:', error);
        }
      }
    };

    if (isClerkLoaded && isSignedIn) {
      handleClerkAuth();
    }
  }, [isClerkLoaded, isSignedIn, user, organization, getToken, dispatch]);

  // Mostrar loader mientras Clerk o la app se inicializan
  if (!isClerkLoaded || isInitializing) {
    return <Loader active size="massive" />;
  }

  // Si no está autenticado con Clerk, mostrar loader (la redirección ya se hizo en useEffect)
  if (!isSignedIn) {
    return <Loader active size="massive" content="Redirecting to DKC Login..." />;
  }

  // Si está autenticado, mostrar loader mientras se sincroniza
  return <Loader active size="massive" />;
});

ClerkLoginWrapper.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
};

export default ClerkLoginWrapper;
